import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import api from "/imports/api/client";

// Survives the auth round-trip (sign-in / sign-up + email verify) so the user
// lands back on this booking afterwards. VerifyEmailView consumes it.
export const POST_AUTH_REDIRECT_KEY = "widget_v2_post_auth_redirect";

// Gate sign-in, resolve the shared link into a cart item, then hand off to the
// existing checkout — a partner-shared booking must reuse the normal pay flow.
export default function useJoinBooking() {
  const { slug, setCart } = useWidget();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { bookishId } = useParams();
  const navigate = useNavigate();

  const [error, setError] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (authLoading || !slug || !bookishId) return;

    // Not signed in yet — remember the target and send them to sign in / sign up
    // (with email verification). They return here once authenticated.
    if (!isAuthenticated) {
      localStorage.setItem(POST_AUTH_REDIRECT_KEY, `/${slug}/join/${bookishId}`);
      navigate(`/${slug}/login?redirect=join/${bookishId}`, { replace: true });
      return;
    }

    if (ran.current) return;
    ran.current = true;
    localStorage.removeItem(POST_AUTH_REDIRECT_KEY);

    api
      .getBookingJoinInfo(slug, bookishId)
      .then((res) => {
        if (!res?.isAvailable || !res.booking) {
          setError(res?.msg || "This booking is no longer available.");
          return;
        }

        const b = res.booking;
        setCart({
          type: b.type,
          items: [
            {
              type: b.type,
              bookishId: b.bookishId,
              name: b.title,
              date: b.date,
              time: b.time,
              duration: b.duration,
              trainerId: b.trainerId,
              trainerName: b.trainerName,
              facilityId: b.facilityId,
              facilityName: b.facilityName,
              serviceId: b.serviceId,
              pricingId: b.pricingId,
              price: b.price,
              currency: b.currency,
              locationId: b.locationId,
              // Split seat: the share is card-only (the capture math sums seat
              // invoice totals), so checkout must not offer credits for it.
              split: b.split || null
            }
          ]
        });
        // Keep the bookishId in the checkout URL so a refresh / bookmark can
        // rebuild the (in-memory) cart instead of showing an empty checkout.
        navigate(`/${slug}/checkout?bookishId=${bookishId}`, { replace: true });
      })
      .catch((err) => {
        setError(err.message || "We couldn't open this booking. Please try again.");
      });
  }, [authLoading, isAuthenticated, slug, bookishId, navigate, setCart]);

  return { slug, error, navigate };
}
